import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Plus, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUIStore } from '@/stores/uiStore';
import { cn } from '@/lib/utils';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { Ingredient, IngredientCategory, IngredientSortOption, IngredientVendor, IngredientRecipeUsage } from '@/types/api';
import {
  INGREDIENT_CATEGORIES,
  INGREDIENT_CATEGORY_LABELS,
} from '@/types/api';
import { QUANTITY_MIN_POSITIVE, VALID_UNITS, getAutoConversionMultiplier, formatScaledQuantity,
  applyUnitSuggestion,
  getDefaultUnitForCategory,
  type UnitSource,
} from '@/types/units';
import { createIngredient, fetchIngredient, fetchIngredients, fetchUnitSuggestion, updateIngredient } from '@/lib/api/ingredients';
import type { UnitSuggestionResponse } from '@/types/api';
import { UnitConversionDialog } from '@/components/admin/UnitConversionDialog';
import { UnitGroupSelectContent } from '@/components/admin/UnitGroupSelectContent';
import { IngredientVendorsSection } from '@/components/admin/IngredientVendorsSection';
import { IngredientImageCarousel } from '@/components/admin/IngredientImageCarousel';
import { EditorLoadingPane } from './EditorLoadingPane';
import { LibraryPaginationFooter } from './LibraryPaginationFooter';
import { MobileEditorDrawer } from './MobileEditorDrawer';
import { useMediaQuery } from '@/hooks/useMediaQuery';

/** Largest value the staple target column stores: @db.Decimal(10, 4). */
const STAPLE_TARGET_MAX = 999999.9999;
const STAPLE_TARGET_RANGE_MESSAGE = 'Enter a weekly target between 0.0001 and 999,999.9999';
const ORDER_INCREMENT_RANGE_MESSAGE = 'Enter an order increment between 0.0001 and 999,999.9999';

const ingredientFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  category: z.enum(INGREDIENT_CATEGORIES as [IngredientCategory, ...IngredientCategory[]]),
  defaultUnit: z.enum(VALID_UNITS, { message: 'Please select a valid unit' }),
  notes: z.string().max(5000).optional(),
  isActive: z.boolean(),
  isStaple: z.boolean(),
  // Kept as text so the input can be cleared while typing; validated below.
  stapleTargetQuantity: z.string(),
  // Order increment (issue #140): pack size in the default unit; 1 = whole units.
  orderIncrement: z.string(),
}).superRefine((values, ctx) => {
  const increment = Number(values.orderIncrement);
  if (
    values.orderIncrement.trim() === ''
    || !Number.isFinite(increment)
    || increment < QUANTITY_MIN_POSITIVE
    || increment > STAPLE_TARGET_MAX
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['orderIncrement'],
      message: ORDER_INCREMENT_RANGE_MESSAGE,
    });
  }
  if (!values.isStaple) return;
  const parsed = Number(values.stapleTargetQuantity);
  if (
    values.stapleTargetQuantity.trim() === ''
    || !Number.isFinite(parsed)
    || parsed < QUANTITY_MIN_POSITIVE
    || parsed > STAPLE_TARGET_MAX
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['stapleTargetQuantity'],
      message: STAPLE_TARGET_RANGE_MESSAGE,
    });
  }
});

type IngredientFormValues = z.infer<typeof ingredientFormSchema>;
type IngredientStatusFilter = 'active' | 'inactive' | 'all';
const INGREDIENT_PAGE_SIZE = 12;
interface LoadIngredientsOptions {
  page?: number;
  search?: string;
  categoryFilter?: IngredientCategory | 'all';
  statusFilter?: IngredientStatusFilter;
  sortBy?: IngredientSortOption;
}

const sortLabels: Record<IngredientSortOption, string> = {
  name: 'Name',
  category: 'Category',
  unit: 'Unit',
};

export function IngredientLibrarySection() {
  const { showToast } = useUIStore();
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const { searchQuery, debouncedSearch, setSearchQuery, resetSearch } = useDebouncedSearch();
  const [categoryFilter, setCategoryFilter] = useState<IngredientCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<IngredientStatusFilter>('active');
  const [sortBy, setSortBy] = useState<IngredientSortOption>('name');
  const [selectedIngredientId, setSelectedIngredientId] = useState<number | null>(null);
  const [selectedIngredientFallback, setSelectedIngredientFallback] = useState<Ingredient | null>(null);
  const [creating, setCreating] = useState(false);
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const selectedIngredientIdRef = useRef<number | null>(null);
  const latestRequestIdRef = useRef(0);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  selectedIngredientIdRef.current = selectedIngredientId;

  const loadIngredients = useCallback(async (options: LoadIngredientsOptions = {}) => {
    const nextPage = options.page ?? page;
    const nextSearch = options.search ?? debouncedSearch;
    const nextCategoryFilter = options.categoryFilter ?? categoryFilter;
    const nextStatusFilter = options.statusFilter ?? statusFilter;
    const nextSortBy = options.sortBy ?? sortBy;

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    try {
      setLoading(true);
      const response = await fetchIngredients({
        page: nextPage,
        pageSize: INGREDIENT_PAGE_SIZE,
        search: nextSearch || undefined,
        category: nextCategoryFilter !== 'all' ? nextCategoryFilter : undefined,
        activeOnly:
          nextStatusFilter === 'all'
            ? 'all'
            : nextStatusFilter === 'active',
        sortBy: nextSortBy,
        includeVendorInfo: true,
      });
      const responseTotalPages = Math.ceil(response.total / INGREDIENT_PAGE_SIZE);

      if (requestId !== latestRequestIdRef.current) {
        return [];
      }

      if (nextPage > 1 && responseTotalPages > 0 && nextPage > responseTotalPages) {
        setPage(responseTotalPages);
        return [];
      }

      setIngredients(response.ingredients);
      setTotal(response.total);

      if (
        selectedIngredientIdRef.current
        && response.ingredients.some((ingredient) => ingredient.id === selectedIngredientIdRef.current)
      ) {
        setSelectedIngredientFallback(null);
      }
      return response.ingredients;
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) {
        return [];
      }

      showToast(
        error instanceof Error ? error.message : 'Failed to load ingredients',
        'error'
      );
      return [];
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [categoryFilter, debouncedSearch, page, showToast, sortBy, statusFilter]);

  useEffect(() => {
    void loadIngredients();
  }, [loadIngredients]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryFilter, sortBy, statusFilter]);

  useEffect(() => {
    if (isDesktop && mobileEditorOpen) {
      setMobileEditorOpen(false);
    }
  }, [isDesktop, mobileEditorOpen]);

  useEffect(() => {
    if (creating) return;

    if (ingredients.length === 0) {
      setSelectedIngredientId(null);
      setMobileEditorOpen(false);
      return;
    }

    const hasSelectionOnPage = selectedIngredientId
      ? ingredients.some((ingredient) => ingredient.id === selectedIngredientId)
      : false;
    const hasFallbackSelection = selectedIngredientFallback?.id === selectedIngredientId;

    if (!selectedIngredientId || (!hasSelectionOnPage && !hasFallbackSelection)) {
      const firstIngredient = ingredients[0];
      if (firstIngredient) {
        setSelectedIngredientId(firstIngredient.id);
      }
    }
  }, [creating, ingredients, selectedIngredientFallback, selectedIngredientId]);

  const selectedIngredient = useMemo(
    () =>
      ingredients.find((ingredient) => ingredient.id === selectedIngredientId)
      ?? (selectedIngredientFallback?.id === selectedIngredientId ? selectedIngredientFallback : null),
    [ingredients, selectedIngredientFallback, selectedIngredientId]
  );

  const startCreate = () => {
    setCreating(true);
    setSelectedIngredientId(null);
    setSelectedIngredientFallback(null);
    setMobileEditorOpen(true);
  };

  const selectIngredient = (ingredientId: number) => {
    setCreating(false);
    setSelectedIngredientId(ingredientId);
    setSelectedIngredientFallback(null);
    setMobileEditorOpen(true);
  };

  const closeEditor = () => {
    setCreating(false);
    setMobileEditorOpen(false);
  };

  const handleIngredientSaved = async (savedIngredient: Ingredient, mode: 'create' | 'edit') => {
    const ingredientWithLibraryMetadata: Ingredient = {
      ...savedIngredient,
      vendorCount: savedIngredient.vendorCount ?? (mode === 'create' ? 0 : selectedIngredient?.vendorCount ?? 0),
      preferredVendorName: savedIngredient.preferredVendorName ?? (mode === 'create' ? null : selectedIngredient?.preferredVendorName ?? null),
    };

    if (mode === 'create') {
      resetSearch();
      setCategoryFilter('all');
      setStatusFilter('active');
      setCreating(false);
      setPage(1);
      setSelectedIngredientId(savedIngredient.id);
      const refreshedIngredients = await loadIngredients({
        page: 1,
        search: '',
        categoryFilter: 'all',
        statusFilter: 'active',
      });
      setSelectedIngredientFallback(
        refreshedIngredients.some((ingredient) => ingredient.id === savedIngredient.id)
          ? null
          : ingredientWithLibraryMetadata
      );
    } else {
      const refreshedIngredients = await loadIngredients();
      setSelectedIngredientFallback(
        refreshedIngredients.some((ingredient) => ingredient.id === savedIngredient.id)
          ? null
          : ingredientWithLibraryMetadata
      );
    }

    setMobileEditorOpen(true);
  };

  const handleVendorAssociationChange = useCallback((ingredientId: number, vendors: IngredientVendor[]) => {
    const preferredVendorName = vendors.find((vendor) => vendor.isPreferred)?.vendor?.name ?? null;

    setIngredients((previous) =>
      previous.map((ingredient) => {
        if (
          ingredient.id !== ingredientId ||
          (
            ingredient.vendorCount === vendors.length &&
            ingredient.preferredVendorName === preferredVendorName
          )
        ) {
          return ingredient;
        }

        return {
          ...ingredient,
          vendorCount: vendors.length,
          preferredVendorName,
        };
      })
    );

    setSelectedIngredientFallback((previous) => {
      if (!previous || previous.id !== ingredientId) {
        return previous;
      }

      if (
        previous.vendorCount === vendors.length
        && previous.preferredVendorName === preferredVendorName
      ) {
        return previous;
      }

      return {
        ...previous,
        vendorCount: vendors.length,
        preferredVendorName,
      };
    });
  }, []);
  const shouldShowLoadingPane = !creating && !selectedIngredient && (loading || ingredients.length > 0);
  const mobileEditorTitle = creating ? 'Create Ingredient' : selectedIngredient?.name ?? 'Ingredient details';
  const editorPane = shouldShowLoadingPane ? (
    <EditorLoadingPane
      title="Loading ingredient"
      description="Fetching the first ingredient for this view."
    />
  ) : (
    <IngredientEditorPane
      ingredient={creating ? null : selectedIngredient}
      onSaved={handleIngredientSaved}
      onVendorAssociationChange={handleVendorAssociationChange}
      showHeader={isDesktop}
    />
  );

  return (
    <>
      <div className="grid gap-4 md:grid-cols-[minmax(280px,2fr)_minmax(200px,3fr)]">
        <Card className="flex min-h-[620px]">
          <CardHeader className="space-y-4 border-b">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle>Ingredients</CardTitle>
                <CardDescription>
                  {loading ? 'Loading ingredients...' : `${total} ingredient${total === 1 ? '' : 's'}`}
                </CardDescription>
              </div>
              <Button size="sm" onClick={startCreate}>
                <Plus className="h-4 w-4" />
                New
              </Button>
            </div>

            <div className="space-y-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search ingredients..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="grid gap-3">
                <Select
                  value={categoryFilter}
                  onValueChange={(value) => setCategoryFilter(value as IngredientCategory | 'all')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {INGREDIENT_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {INGREDIENT_CATEGORY_LABELS[category]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value as IngredientStatusFilter)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={sortBy}
                  onValueChange={(value) => setSortBy(value as IngredientSortOption)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(sortLabels) as IngredientSortOption[]).map((sort) => (
                      <SelectItem key={sort} value={sort}>
                        Sort: {sortLabels[sort]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex-1 overflow-y-auto px-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
              </div>
            ) : ingredients.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="mb-4 text-sm text-muted-foreground">
                  {debouncedSearch ? `No ingredients found matching "${debouncedSearch}"` : 'No ingredients found'}
                </p>
                <Button size="sm" onClick={startCreate}>Create First Ingredient</Button>
              </div>
            ) : (
              <div className="px-4 py-3">
                <div className="mb-2 grid grid-cols-[minmax(0,1fr)_96px] gap-3 px-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <span>Name</span>
                  <span>Unit</span>
                </div>

                <div className="space-y-2" role="listbox" aria-label="Ingredients">
                  {ingredients.map((ingredient) => {
                    const isSelected = !creating && selectedIngredientId === ingredient.id;

                    return (
                      <div
                        key={ingredient.id}
                        role="option"
                        aria-selected={isSelected}
                        className={cn(
                          'grid grid-cols-[minmax(0,1fr)_96px] items-center gap-3 rounded-lg border px-2 py-2 transition-colors',
                          isSelected ? 'border-primary/40 bg-primary/5' : 'hover:bg-muted/60',
                          ingredient.isActive ? '' : 'opacity-60'
                        )}
                      >
                        <button
                          type="button"
                          className="min-w-0 text-left"
                          onClick={() => selectIngredient(ingredient.id)}
                        >
                          <p className="truncate font-medium text-foreground" title={ingredient.name}>
                            {ingredient.name}
                          </p>
                        </button>

                        <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
                          {ingredient.isStaple ? <Badge variant="secondary">Staple</Badge> : null}
                          <span>{ingredient.defaultUnit}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>

          <LibraryPaginationFooter
            page={page}
            pageSize={INGREDIENT_PAGE_SIZE}
            total={total}
            loading={loading}
            onPageChange={setPage}
          />
        </Card>

        {isDesktop ? editorPane : null}

        {!isDesktop ? (
          <MobileEditorDrawer
            open={mobileEditorOpen}
            onOpenChange={setMobileEditorOpen}
            onClose={closeEditor}
            title={mobileEditorTitle}
          >
            {editorPane}
          </MobileEditorDrawer>
        ) : null}
      </div>
    </>
  );
}

interface IngredientEditorPaneProps {
  ingredient: Ingredient | null;
  onSaved: (ingredient: Ingredient, mode: 'create' | 'edit') => Promise<void>;
  onVendorAssociationChange?: (ingredientId: number, vendors: IngredientVendor[]) => void;
  showHeader?: boolean;
}

function IngredientEditorPane({
  ingredient,
  onSaved,
  onVendorAssociationChange,
  showHeader = true,
}: IngredientEditorPaneProps) {
  const { showToast } = useUIStore();
  const [saving, setSaving] = useState(false);
  const [imageUrls, setImageUrls] = useState<string[]>(ingredient?.imageUrls ?? []);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
  const [recipeUsages, setRecipeUsages] = useState<IngredientRecipeUsage[]>([]);
  const [loadingUsages, setLoadingUsages] = useState(false);
  const [usagesLoadFailed, setUsagesLoadFailed] = useState(false);
  const pendingFormValuesRef = useRef<IngredientFormValues | null>(null);
  const usagesFetchIdRef = useRef(0);
  const isEditMode = !!ingredient;

  const addImageUrl = () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    try {
      new URL(url);
      setImageUrls((prev) => [...prev, url]);
      setImageUrlInput('');
    } catch {
      showToast('Please enter a valid URL', 'error');
    }
  };

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IngredientFormValues>({
    resolver: zodResolver(ingredientFormSchema),
    defaultValues: buildIngredientDefaults(ingredient),
  });

  useEffect(() => {
    reset(buildIngredientDefaults(ingredient));
    setImageUrls(ingredient?.imageUrls ?? []);
    setImageUrlInput('');
    setRecipeUsages([]);
    markUnitTouched(false);
    setUnitNote(null);
    setDriftHint(null);
    suggestionSeq.current += 1; // any suggestion still in flight belongs to the previous pane
  }, [ingredient, reset]);

  // ---- Unit suggestion (PR 165) ----
  // A unit the user picked is never overwritten; suggestions only fill an untouched
  // field. The flag is mirrored in a ref so an in-flight suggestion reads the value
  // at the moment it resolves, not the value when the request was scheduled.
  const [unitTouched, setUnitTouched] = useState(false);
  const unitTouchedRef = useRef(false);
  // Where the current unit came from: the category rule, a name/history suggestion, or the user.
  const unitSourceRef = useRef<UnitSource>('category');
  const markUnitTouched = (touched: boolean) => {
    unitTouchedRef.current = touched;
    setUnitTouched(touched);
    if (touched) unitSourceRef.current = 'user';
    else unitSourceRef.current = 'category';
  };
  const categoryRef = useRef<IngredientCategory>('PRODUCE');
  const [unitNote, setUnitNote] = useState<string | null>(null);
  const [driftHint, setDriftHint] = useState<{ unit: string; used: number; total: number } | null>(null);
  const suggestionSeq = useRef(0);
  const nameValue = watch('name');
  const latestNameRef = useRef('');
  latestNameRef.current = (nameValue ?? '').trim();

  // New ingredient: look the name up (history of a same-named ingredient, or the herb rule).
  // Every change bumps the sequence, so a request started for an earlier name, or
  // before the pane switched to edit mode, can no longer write into the form.
  useEffect(() => {
    const seq = ++suggestionSeq.current;
    if (isEditMode) return;
    const name = (nameValue ?? '').trim();
    if (name.length < 2) {
      setUnitNote(null);
      return;
    }
    const timer = setTimeout(async () => {
      let result: UnitSuggestionResponse;
      try {
        result = await fetchUnitSuggestion({ name });
      } catch {
        return;
      }
      // Drop the response if a newer request started or the typed name has moved on.
      if (seq !== suggestionSeq.current || latestNameRef.current !== name) return;
      const { unit } = describeSuggestion(result, { applied: false });
      const next = applyUnitSuggestion({ suggestedUnit: unit, source: unitSourceRef.current, category: categoryRef.current });
      if (next) {
        unitSourceRef.current = next.source;
        setValue('defaultUnit', next.unit, { shouldDirty: true });
      }
      setUnitNote(describeSuggestion(result, { applied: next?.source === 'suggestion' }).note);
    }, 400);
    return () => clearTimeout(timer);
  }, [nameValue, isEditMode, setValue]);

  // Existing ingredient: say when a majority of its recipes use another unit than the default.
  useEffect(() => {
    if (!isEditMode || !ingredient) return;
    let cancelled = false;
    fetchUnitSuggestion({ ingredientId: ingredient.id })
      .then((result) => {
        if (cancelled) return;
        const total = result.allTimeUnits.reduce((sum, entry) => sum + entry.count, 0);
        const top = result.allTimeUnits[0];
        const currentCount = result.allTimeUnits.find((entry) => entry.unit === ingredient.defaultUnit)?.count ?? 0;
        if (top && top.unit !== ingredient.defaultUnit && top.count > currentCount && top.count * 2 > total) {
          setDriftHint({ unit: top.unit, used: top.count, total });
        } else {
          setDriftHint(null);
        }
      })
      .catch(() => { /* hint only */ });
    return () => { cancelled = true; };
  }, [isEditMode, ingredient]);

  const loadRecipeUsages = useCallback((ingredientId: number) => {
    const fetchId = ++usagesFetchIdRef.current;
    setLoadingUsages(true);
    setUsagesLoadFailed(false);
    fetchIngredient(ingredientId, { includeRecipeUsages: true })
      .then((data) => {
        if (fetchId === usagesFetchIdRef.current) {
          setRecipeUsages(data.recipeUsages ?? []);
        }
      })
      .catch(() => {
        if (fetchId === usagesFetchIdRef.current) {
          setRecipeUsages([]);
          setUsagesLoadFailed(true);
        }
      })
      .finally(() => {
        if (fetchId === usagesFetchIdRef.current) {
          setLoadingUsages(false);
        }
      });
  }, []);

  // Fetch recipe usages when a different ingredient is selected
  useEffect(() => {
    if (!ingredient) {
      setRecipeUsages([]);
      return;
    }
    loadRecipeUsages(ingredient.id);
  }, [ingredient?.id, loadRecipeUsages]);

  const categoryValue = watch('category');
  const defaultUnitValue = watch('defaultUnit');
  categoryRef.current = categoryValue;
  const isActive = watch('isActive');
  const isStaple = watch('isStaple');

  const submitWithMultiplier = async (values: IngredientFormValues, unitConversionMultiplier?: number) => {
    try {
      setSaving(true);

      const payload = {
        name: values.name.trim(),
        category: values.category,
        defaultUnit: values.defaultUnit,
        notes: values.notes?.trim() || null,
        isActive: values.isActive,
        imageUrls,
        isStaple: values.isStaple,
        stapleTargetQuantity: values.isStaple ? Number(values.stapleTargetQuantity) : null,
        orderIncrement: Number(values.orderIncrement),
        ...(unitConversionMultiplier !== undefined && { unitConversionMultiplier }),
      };

      const savedIngredient = isEditMode && ingredient
        ? await updateIngredient(ingredient.id, payload)
        : await createIngredient(payload);

      const recipesMsg = savedIngredient.recipesUpdated
        ? ` (${savedIngredient.recipesUpdated} recipe${savedIngredient.recipesUpdated === 1 ? '' : 's'} updated)`
        : '';
      showToast(
        isEditMode ? `Ingredient updated successfully${recipesMsg}` : 'Ingredient created successfully',
        'success'
      );

      await onSaved(savedIngredient, isEditMode ? 'edit' : 'create');

      // Refetch recipe usages if the unit changed (quantities may have been scaled)
      if (unitConversionMultiplier !== undefined && savedIngredient.id) {
        loadRecipeUsages(savedIngredient.id);
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : `Failed to ${isEditMode ? 'update' : 'create'} ingredient`,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = async (values: IngredientFormValues) => {
    if (!isEditMode || !ingredient || values.defaultUnit === ingredient.defaultUnit) {
      return submitWithMultiplier(values);
    }

    // Unit changed — try auto-conversion
    const autoMultiplier = getAutoConversionMultiplier(ingredient.defaultUnit, values.defaultUnit);
    if (autoMultiplier !== null) {
      return submitWithMultiplier(values, autoMultiplier);
    }

    // Cross-group change — skip dialog only if usages loaded successfully and confirmed empty
    if (!loadingUsages && !usagesLoadFailed && recipeUsages.length === 0) {
      return submitWithMultiplier(values);
    }

    // Show conversion dialog for user to enter multiplier
    pendingFormValuesRef.current = values;
    setConversionDialogOpen(true);
  };

  const handleConversionConfirm = (multiplier: number) => {
    setConversionDialogOpen(false);
    const values = pendingFormValuesRef.current;
    if (values) {
      pendingFormValuesRef.current = null;
      void submitWithMultiplier(values, multiplier);
    }
  };

  const handleConversionCancel = () => {
    setConversionDialogOpen(false);
    pendingFormValuesRef.current = null;
    if (ingredient) {
      setValue('defaultUnit', ingredient.defaultUnit, { shouldDirty: false });
    }
  };

  const handleVendorChange = useCallback((vendors: IngredientVendor[]) => {
    if (!ingredient?.id) return;
    onVendorAssociationChange?.(ingredient.id, vendors);
  }, [ingredient?.id, onVendorAssociationChange]);

  return (
    <Card className="min-h-[620px]">
      {showHeader ? (
        <CardHeader className="border-b">
          <CardTitle>{isEditMode ? ingredient.name : 'Create Ingredient'}</CardTitle>
        </CardHeader>
      ) : null}

      <CardContent className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="ingredient-name">Ingredient Name</Label>
            <Input
              id="ingredient-name"
              {...register('name')}
              placeholder="e.g. Basmati Rice"
              disabled={saving}
            />
            {errors.name ? <p className="text-sm text-destructive">{errors.name.message}</p> : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_160px]">
            <div className="space-y-2">
              <Label htmlFor="ingredient-category">Category</Label>
              <Select
                value={categoryValue}
                onValueChange={(value) => {
                  const nextCategory = value as IngredientCategory;
                  // A new ingredient's unit follows its category until the user picks one.
                  if (!isEditMode && !unitTouched && unitSourceRef.current !== 'suggestion') {
                    setValue('defaultUnit', getDefaultUnitForCategory(nextCategory) ?? '', { shouldDirty: true });
                  }
                  setValue('category', nextCategory, { shouldDirty: true });
                }}
                disabled={saving}
              >
                <SelectTrigger id="ingredient-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {INGREDIENT_CATEGORIES.map((category) => (
                    <SelectItem key={category} value={category}>
                      {INGREDIENT_CATEGORY_LABELS[category]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category ? <p className="text-sm text-destructive">{errors.category.message}</p> : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ingredient-unit">Default Unit</Label>
              <Select
                value={defaultUnitValue}
                onValueChange={(value) => {
                  markUnitTouched(true);
                  setValue('defaultUnit', value, { shouldDirty: true });
                }}
                disabled={saving}
              >
                <SelectTrigger id="ingredient-unit">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <UnitGroupSelectContent />
              </Select>
              {errors.defaultUnit ? <p className="text-sm text-destructive">{errors.defaultUnit.message}</p> : null}
              {unitNote ? <p className="text-xs text-muted-foreground">{unitNote}</p> : null}
              {driftHint ? (
                <div className="flex flex-wrap items-center gap-2 text-xs text-amber-700">
                  <span>
                    Used as {driftHint.unit} in {driftHint.used} of {driftHint.total} recipes; default is {defaultUnitValue || 'not set'}.
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={saving}
                    onClick={() => {
                      markUnitTouched(true);
                      setValue('defaultUnit', driftHint.unit, { shouldDirty: true });
                      setDriftHint(null);
                    }}
                  >
                    Use {driftHint.unit}
                  </Button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ingredient-notes">Notes</Label>
            <Textarea
              id="ingredient-notes"
              {...register('notes')}
              placeholder="Optional sourcing or handling notes..."
              rows={4}
              disabled={saving}
            />
            {errors.notes ? <p className="text-sm text-destructive">{errors.notes.message}</p> : null}
          </div>

          <div className="space-y-2">
            <Label>Images</Label>
            <IngredientImageCarousel
              images={imageUrls}
              onRemove={(index) => setImageUrls((prev) => prev.filter((_, i) => i !== index))}
            />
            <div className="flex gap-2">
              <Input
                placeholder="https://example.com/image.jpg"
                value={imageUrlInput}
                onChange={(event) => setImageUrlInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addImageUrl();
                  }
                }}
                disabled={saving}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={saving || !imageUrlInput.trim()}
                onClick={addImageUrl}
              >
                Add
              </Button>
            </div>
          </div>

          <div className="rounded-lg border bg-muted/30 px-4 py-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(event) => setValue('isActive', event.target.checked, { shouldDirty: true })}
                className="h-4 w-4 rounded border-gray-300"
                disabled={saving}
              />
              Active for recipes and procurement planning
            </label>
          </div>

          <div className="space-y-2 rounded-lg border bg-muted/30 px-4 py-3">
            <Label htmlFor="ingredient-order-increment">
              Order increment{defaultUnitValue ? ` (${defaultUnitValue})` : ''}
            </Label>
            <Input
              id="ingredient-order-increment"
              inputMode="decimal"
              {...register('orderIncrement')}
              placeholder="1"
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              Smallest amount you can buy (a bag, case or pack). Procurement rounds the purchase quantity up to a multiple of it; 1 means whole units. Converted automatically when the default unit changes.
            </p>
            {errors.orderIncrement ? <p className="text-sm text-destructive">{errors.orderIncrement.message}</p> : null}
          </div>

          <div className="space-y-3 rounded-lg border bg-muted/30 px-4 py-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isStaple}
                onChange={(event) => setValue('isStaple', event.target.checked, { shouldDirty: true, shouldValidate: true })}
                className="h-4 w-4 rounded border-gray-300"
                disabled={saving}
              />
              Weekly staple (added to every week&apos;s procurement list)
            </label>
            {isStaple ? (
              <div className="space-y-2">
                <Label htmlFor="ingredient-staple-target">
                  Weekly target stock{defaultUnitValue ? ` (${defaultUnitValue})` : ''}
                </Label>
                <Input
                  id="ingredient-staple-target"
                  inputMode="decimal"
                  {...register('stapleTargetQuantity')}
                  placeholder="e.g. 15"
                  disabled={saving}
                />
                <p className="text-xs text-muted-foreground">
                  Each week the list suggests buying the target minus what is on hand.
                </p>
                {errors.stapleTargetQuantity ? <p className="text-sm text-destructive">{errors.stapleTargetQuantity.message}</p> : null}
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Ingredient'}
            </Button>
          </div>
        </form>

        {ingredient ? (
          <div className="mt-6 border-t pt-6">
            <IngredientVendorsSection
              ingredientId={ingredient.id}
              onChange={handleVendorChange}
            />
          </div>
        ) : null}

        {ingredient ? (
          <div className="mt-6 border-t pt-6">
            <h4 className="mb-3 text-sm font-semibold">Used in Recipes</h4>
            {loadingUsages ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : recipeUsages.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not used in any recipes</p>
            ) : (
              <ul className="space-y-1">
                {recipeUsages.map((usage) => (
                  <li key={usage.recipeId} className="flex items-center justify-between text-sm">
                    <span className="truncate">{usage.recipeName}</span>
                    <span className="ml-2 shrink-0 text-muted-foreground">
                      {usage.quantityPerLarge
                        ? `${formatScaledQuantity(parseFloat(usage.quantityPerLarge))} ${usage.unit}`
                        : usage.unit}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </CardContent>

      {isEditMode && ingredient && (
        <UnitConversionDialog
          open={conversionDialogOpen}
          onOpenChange={setConversionDialogOpen}
          oldUnit={ingredient.defaultUnit}
          newUnit={defaultUnitValue}
          recipeUsages={recipeUsages}
          onConfirm={handleConversionConfirm}
          onCancel={handleConversionCancel}
        />
      )}
    </Card>
  );
}

/**
 * Turn a unit-suggestion response into the unit to prefill (or null) and a short
 * note explaining it. Shared wording with the recipe quick-create.
 */
export function describeSuggestion(result: UnitSuggestionResponse, opts: { applied: boolean }): { unit: string | null; note: string | null } {
  const match = result.matchedIngredient;
  if (match) {
    const unit = result.suggestion?.unit ?? match.defaultUnit;
    const usage = result.suggestion
      ? `normally used in ${result.suggestion.unit} (${result.suggestion.used} of the last ${result.suggestion.of} uses)`
      : `stored in ${match.defaultUnit}`;
    const note = match.isActive
      ? `An ingredient named "${match.name}" already exists, ${usage}. Names must be unique; edit that one instead.`
      : `Same name as the retired ingredient "${match.name}", ${usage}. Names must be unique; reactivate it instead of creating a new one.`;
    return { unit, note };
  }
  if (result.byName) {
    const noun = result.byName === 'packs' ? 'by the pack' : 'by the bunch';
    const note = opts.applied
      ? `Fresh herbs are bought ${noun}; unit set to ${result.byName}.`
      : `Fresh herbs are usually bought ${noun}.`;
    return { unit: result.byName, note };
  }
  return { unit: null, note: null };
}

function buildIngredientDefaults(ingredient: Ingredient | null): IngredientFormValues {
  return {
    name: ingredient?.name ?? '',
    category: ingredient?.category ?? 'PRODUCE',
    // New ingredients start with the unit the kitchen uses for the category.
    defaultUnit: ingredient?.defaultUnit ?? (getDefaultUnitForCategory('PRODUCE') ?? ''),
    notes: ingredient?.notes ?? '',
    isActive: ingredient?.isActive ?? true,
    isStaple: ingredient?.isStaple ?? false,
    stapleTargetQuantity: ingredient?.stapleTargetQuantity ? formatScaledQuantity(Number(ingredient.stapleTargetQuantity)) : '',
    orderIncrement: ingredient?.orderIncrement ? formatScaledQuantity(Number(ingredient.orderIncrement)) : '1',
  };
}
